import { Languages, LogOut, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { i18n, LANGUAGE_OPTIONS, resolveSupportedLanguage, type SupportedLanguage } from '@/lib/i18n';
import { useAuth } from '@/providers/useAuth';

export function LanguageMenuSub() {
    const { t } = useTranslation();
    const currentLanguage = resolveSupportedLanguage(i18n.language);

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger>
                <Languages />
                <span>{t('sidebar.language')}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
                <DropdownMenuLabel>{t('sidebar.languageSelector')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                    value={currentLanguage}
                    onValueChange={(value) => void i18n.changeLanguage(value as SupportedLanguage)}
                >
                    {LANGUAGE_OPTIONS.map((language) => (
                        <DropdownMenuRadioItem key={language.value} value={language.value}>
                            <span>{language.label}</span>
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
}

export function AccountMenu() {
    const { t } = useTranslation();
    const { email, logout, username } = useAuth();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    type='button'
                    variant='accountAvatar'
                    size='icon'
                    aria-label={t('sidebar.account')}
                >
                    <Avatar className='h-7 w-7'>
                        <AvatarFallback variant='sidebarRoundPlain'>
                            {(username ?? t('common.guest')).slice(0, 1).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-64'>
                <DropdownMenuLabel>
                    <div className='flex items-center gap-2'>
                        <User className='h-4 w-4' />
                        <div className='min-w-0'>
                            <div className='truncate text-sm font-medium'>{username ?? t('common.guest')}</div>
                            {email ? <div className='truncate text-xs text-muted-foreground'>{email}</div> : null}
                        </div>
                    </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <LanguageMenuSub />
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void logout()}>
                    <LogOut />
                    <span>{t('common.logout')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
